'use client';
import React from 'react';
import { ArticlePageContext } from '../../context';
import { Impression } from '@benzinga/analytics';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { performRegexesOnCampaignHTML } from '@benzinga/article-manager';
import { Skeleton } from '@benzinga/core-ui';

interface Props {
  placeholderId: string;
  nodeID: string;
  strategy?: string;
  ticker: string;
  noSnippet?: boolean;
}

export const CampaignifyBlock: React.FC<Props> = ({ nodeID, noSnippet = true, placeholderId, ticker }) => {
  const [productSlug, setProductSlug] = React.useState<string>('Unknown');
  const [campaignHtml, setCampaignHtml] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const article = React.useContext(ArticlePageContext);
  const campaign = article?.campaign;
  const campaignStrategy = article?.campaignStrategy;

  React.useEffect(() => {
    const text = campaign?.[placeholderId];
    if (text) {
      const matches = text.match(/https:\/\/www.benzinga.com\/premium\/ideas\/([A-z-]+)\//);
      if (matches && matches[1]) setProductSlug(matches[1]);
    }
  }, [campaign, placeholderId]);

  React.useEffect(() => {
    try {
      const regExReplace = (text: string) => {
        const suppliedTickerRegEx = new RegExp(/\$\$[A-Z]{3,5}/, 'g');
        const noSuppliedTickerRegEx = new RegExp(/(\$\$)([A-Z]{3,5})/, 'g');
        const matches = text.match(/utm_source=[A-z-]+/);

        if (campaignStrategy && matches) {
          text = text.replaceAll(matches[0], `${matches[0]}&utm_campaign=${campaignStrategy}`);
        }

        if (ticker) {
          return text?.replace(suppliedTickerRegEx, ticker);
        } else {
          return text?.replace(noSuppliedTickerRegEx, '$2');
        }
      };

      if (campaign && placeholderId) {
        const raw = campaign[placeholderId as keyof typeof campaign];
        if (raw) {
          const processed = regExReplace(raw);
          const afterRegexes = performRegexesOnCampaignHTML(processed);
          const sanitized = sanitizeHTML(afterRegexes);
          setCampaignHtml(sanitized);
        } else {
          setCampaignHtml(null);
        }
      } else {
        setCampaignHtml(null);
      }
    } catch (_error) {
      setCampaignHtml(null);
    }
  }, [campaignStrategy, campaign, ticker, placeholderId]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const applyAnchorAttributes = (anchor: HTMLAnchorElement) => {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('data-action', 'In Article Campaignify Link Click');
    };

    container.querySelectorAll('a').forEach(a => applyAnchorAttributes(a as HTMLAnchorElement));

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
              const element = node as Element;
              if (element.tagName.toLowerCase() === 'a') {
                applyAnchorAttributes(element as HTMLAnchorElement);
              }
              element.querySelectorAll?.('a')?.forEach(a => applyAnchorAttributes(a as HTMLAnchorElement));
            }
          });
        }
      }
    });

    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [campaignHtml]);

  if (article.hide || article.campaign === null) return null;

  return (
    <Impression campaign_id={`${placeholderId} ${nodeID}`} tag={productSlug} unit_type="Campaignify Unit">
      <div
        className={`bz-campaign bz-campaign-${placeholderId}-${nodeID}`}
        data-nosnippet={noSnippet ? true : null}
        ref={containerRef}
      >
        {campaignHtml ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(campaignHtml) }} />
        ) : (
          <>
            <Skeleton active paragraph={{ rows: 7 }} />
            <div className="flex sm:hidden flex-col">
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          </>
        )}
      </div>
    </Impression>
  );
};
